
import { useState } from "react"
import { EmailFormData, MailFormData } from "@/types/form"
import { useToast } from "@/components/ui/use-toast"

export const useMultiStepForm = (initialDeliveryMethod: string | null | undefined) => {
  const [step, setStep] = useState(1)
  const [showPayment, setShowPayment] = useState(false)
  const [showErrors, setShowErrors] = useState(false)
  const { toast } = useToast()
  const isEmailDelivery = initialDeliveryMethod === 'email'
  
  const [formData, setFormData] = useState(
    isEmailDelivery
      ? {
          sender: {
            firstName: "",
            lastName: "",
            email: "",
            isAnonymous: false
          },
          recipient: {
            firstName: "",
            lastName: "",
            email: ""
          },
          situation: {
            description: ""
          }
        } as EmailFormData
      : {
          sender: {
            firstName: "",
            lastName: "",
            email: "",
            isAnonymous: false
          },
          recipient: {
            firstName: "",
            lastName: "",
            email: "",
            streetAddress: "",
            unit: "",
            city: "",
            state: "",
            zipCode: ""
          },
          situation: {
            description: ""
          }
        } as MailFormData
  )

  const handleInputChange = (section: 'sender' | 'recipient' | 'situation', field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }))
  }

  const validateCurrentStep = (): boolean => {
    if (isEmailDelivery) {
      switch (step) {
        case 1:
          // If sending anonymously, only email is required, otherwise all fields required
          if (formData.sender.isAnonymous) {
            return Boolean(formData.sender.email)
          }
          return Boolean(formData.sender.firstName && formData.sender.lastName && formData.sender.email)
        case 2:
          return Boolean(formData.recipient.firstName && formData.recipient.lastName && formData.recipient.email)
        case 3:
          return Boolean(formData.situation.description)
        default:
          return false
      }
    } else {
      switch (step) {
        case 1:
          // If sending anonymously, only email is required, otherwise all fields required
          if (formData.sender.isAnonymous) {
            return Boolean(formData.sender.email)
          }
          return Boolean(formData.sender.firstName && formData.sender.lastName && formData.sender.email)
        case 2:
          const recipient = formData.recipient as MailFormData['recipient']
          return Boolean(recipient.firstName && recipient.lastName &&
                 recipient.streetAddress && recipient.city && recipient.state && recipient.zipCode)
        case 3:
          return Boolean(formData.situation.description)
        default:
          return false
      }
    }
  }

  const handleNext = () => {
    setShowErrors(true)
    if (!validateCurrentStep()) {
      toast({
        title: "Required Fields Missing",
        description: "Please fill out all required fields before proceeding.",
        variant: "destructive"
      })
      return
    }
    if (step < 3) {
      setStep(step + 1)
      setShowErrors(false)
    } else {
      setShowPayment(true)
    }
  }

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1)
      setShowErrors(false)
    }
  }

  return {
    step,
    showPayment,
    setShowPayment,
    showErrors,
    formData,
    handleInputChange,
    handleNext,
    handleBack,
    validateCurrentStep,
    isEmailDelivery
  }
}
